import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import {
	DATE_PRESET_CUSTOM,
	computeRelativeDateRange,
} from "utils/relativeDatePresets";

const MOBILE_PRESETS = [
	{ id: "last_7_days", label: "datePresets.last_7_days" },
	{ id: "last_15_days", label: "datePresets.last_15_days" },
	{ id: "last_30_days", label: "datePresets.last_month" },
];

const toMoment = (value) => {
	if (!value) return null;
	if (moment.isMoment(value)) return value.clone();
	return moment(value);
};

const computeMobilePresetRange = (presetId, now = moment()) => {
	if (presetId === "last_15_days") {
		return {
			start: now.clone().subtract(14, "day").startOf("day"),
			end: now.clone().endOf("day"),
		};
	}
	return computeRelativeDateRange(presetId, now);
};

const formatRangeLabel = (start, end, t) => {
	if (!start || !end) return t("relativeDateNav.selectPeriod");

	const isSameDay = start.isSame(end, "day");
	const sameMonth = start.isSame(end, "month");
	const sameYear = start.isSame(end, "year");

	if (isSameDay) {
		return start.format("D MMM YYYY");
	}
	if (sameMonth && sameYear) {
		return `${start.format("D")} - ${end.format("D MMM YYYY")}`;
	}
	if (sameYear) {
		return `${start.format("D MMM")} - ${end.format("D MMM YYYY")}`;
	}
	return `${start.format("D MMM YYYY")} - ${end.format("D MMM YYYY")}`;
};

/**
 * Navegación de periodo para móvil:
 * - Flechas ←/→ para desplazar el rango por un número equivalente de días.
 * - Click en la etiqueta central para abrir los atajos de periodo.
 */
const MobileAttendanceDateNav = ({
	startDate,
	endDate,
	activeDatePreset,
	onDatesChange,
	onPresetChange,
}) => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef(null);

	const start = useMemo(() => toMoment(startDate), [startDate]);
	const end = useMemo(() => toMoment(endDate), [endDate]);
	const label = useMemo(() => formatRangeLabel(start, end, t), [start, end, t]);

	useEffect(() => {
		if (!isOpen) return undefined;
		const handleClickOutside = (event) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target)
			) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isOpen]);

	const shiftRange = (direction) => {
		if (!start || !end) return;
		const daysInRange = Math.max(
			1,
			end.clone().startOf("day").diff(start.clone().startOf("day"), "days") + 1
		);
		onDatesChange(
			{
				startDate: start.clone().add(direction * daysInRange, "days"),
				endDate: end.clone().add(direction * daysInRange, "days"),
			},
			DATE_PRESET_CUSTOM
		);
	};

	const handlePresetClick = (presetId) => {
		const range = computeMobilePresetRange(presetId, moment());
		if (range?.start && range?.end) {
			if (onPresetChange && presetId !== "last_15_days") {
				onPresetChange(presetId);
			} else {
				onDatesChange(
					{ startDate: range.start, endDate: range.end },
					presetId || DATE_PRESET_CUSTOM
				);
			}
		}
		setIsOpen(false);
	};

	return (
		<div className="mobile-attendance-date-nav" ref={containerRef}>
			<button
				type="button"
				className="mobile-attendance-date-nav__arrow"
				onClick={() => shiftRange(-1)}
				aria-label={t("relativeDateNav.previousPeriod")}
			>
				<FiChevronLeft size={20} />
			</button>

			<button
				type="button"
				className="mobile-attendance-date-nav__label"
				onClick={() => setIsOpen((prev) => !prev)}
				title={t("relativeDateNav.selectPeriod")}
			>
				{label}
			</button>

			<button
				type="button"
				className="mobile-attendance-date-nav__arrow"
				onClick={() => shiftRange(1)}
				aria-label={t("relativeDateNav.nextPeriod")}
			>
				<FiChevronRight size={20} />
			</button>

			{isOpen && (
				<div className="mobile-attendance-date-nav__popover">
					{MOBILE_PRESETS.map((preset) => {
						const isSelected = activeDatePreset === preset.id;
						return (
							<button
								key={preset.id}
								type="button"
								onClick={() => handlePresetClick(preset.id)}
								className={`mobile-attendance-date-nav__preset ${
									isSelected
										? "mobile-attendance-date-nav__preset--active"
										: ""
								}`}
							>
								{t(preset.label)}
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
};

MobileAttendanceDateNav.propTypes = {
	startDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	endDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	activeDatePreset: PropTypes.string,
	onDatesChange: PropTypes.func.isRequired,
	onPresetChange: PropTypes.func,
};

MobileAttendanceDateNav.defaultProps = {
	startDate: null,
	endDate: null,
	activeDatePreset: DATE_PRESET_CUSTOM,
	onPresetChange: null,
};

export default MobileAttendanceDateNav;
